import { Copy, Church, Megaphone, DoorOpen, Sparkles, HandHeart, Leaf, Check } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { IMG } from "../lib/images";
import { useSettings } from "../lib/settings";
import PageHeader from "../components/PageHeader";
import { Reveal, SectionHeading } from "../components/motion";

const USES = [
  { icon: Church, label: "La vie de l'église" },
  { icon: Megaphone, label: "Les actions d'évangélisation" },
  { icon: DoorOpen, label: "L'accueil de chacun" },
  { icon: Sparkles, label: "Les activités" },
  { icon: HandHeart, label: "La solidarité" },
  { icon: Leaf, label: "Le développement de l'œuvre" },
];

export default function Giving() {
  const { settings } = useSettings();
  const [copied, setCopied] = useState(false);

  const copyIban = async () => {
    if (!settings.iban) return;
    try {
      await navigator.clipboard.writeText(settings.iban);
      setCopied(true);
      toast.success("IBAN copié dans le presse-papiers.");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Copie impossible — sélectionnez l'IBAN manuellement.");
    }
  };

  return (
    <>
      <PageHeader
        testid="giving-header"
        eyebrow="Donner"
        titleLines={["Donner librement,", "avec le cœur"]}
        intro="Les dons permettent de faire vivre l'église et de manifester concrètement l'amour de Dieu autour de nous. Chacun donne comme il l'a décidé dans son cœur."
        image={IMG.giving}
      />
      <section className="py-16 sm:py-24">
        <div className="container-site">
          <SectionHeading eyebrow="À quoi servent les dons" title="Participer à la vie de la maison" />
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {USES.map((u, i) => (
              <Reveal key={u.label} delay={(i % 3) * 0.07}>
                <div data-testid={`giving-use-${i}`} className="card-warm flex items-center gap-4 p-5">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-terracotta-soft text-terracotta">
                    <u.icon className="h-5 w-5" />
                  </span>
                  <p className="font-serif text-base font-semibold tracking-tight text-obsidian">{u.label}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-10">
            <div className="rounded-2xl border border-dashed border-line bg-sand p-6 sm:p-8">
              <p className="max-w-3xl text-sm leading-relaxed text-stone-600">
                <strong className="font-serif text-base text-obsidian">Un don libre et volontaire. </strong>
                Il n'y a ici aucune pression, aucune culpabilisation et aucune promesse financière en
                échange d'un don. « Que chacun donne comme il l'a résolu en son cœur, sans tristesse
                ni contrainte ; car Dieu aime celui qui donne avec joie. » — 2 Corinthiens 9:7
              </p>
            </div>
          </Reveal>

          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            <Reveal>
              <div data-testid="giving-bank-card" className="card-warm h-full p-8 sm:p-10">
                <p className="label-eyebrow">Virement bancaire</p>
                <h3 className="mt-3 font-serif text-2xl font-semibold tracking-tight text-obsidian">Donner par virement</h3>
                {settings.iban ? (
                  <div className="mt-6 space-y-4">
                    <div className="rounded-xl bg-sand p-5">
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">IBAN</p>
                      <p data-testid="giving-iban" className="mt-1 break-all font-mono text-sm font-semibold text-obsidian">{settings.iban}</p>
                    </div>
                    {settings.bic && (
                      <div className="rounded-xl bg-sand p-5">
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">BIC</p>
                        <p className="mt-1 font-mono text-sm font-semibold text-obsidian">{settings.bic}</p>
                      </div>
                    )}
                    <button data-testid="giving-copy-iban-btn" onClick={copyIban} className="btn-primary">
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      {copied ? "IBAN copié !" : "Copier l'IBAN"}
                    </button>
                    <p className="text-xs leading-relaxed text-stone-500">
                      Titulaire du compte : Église Maison de la Charité — 77144 Montévrain.
                    </p>
                  </div>
                ) : (
                  <p className="mt-5 text-sm leading-relaxed text-stone-600">
                    Les coordonnées bancaires de l'église seront publiées ici prochainement. En
                    attendant, vous pouvez nous les demander directement lors d'une rencontre ou
                    via la page contact.
                  </p>
                )}
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div data-testid="giving-online-card" className="card-warm h-full border-dashed bg-sand p-8 sm:p-10">
                <p className="label-eyebrow">Don en ligne</p>
                <h3 className="mt-3 font-serif text-2xl font-semibold tracking-tight text-obsidian">Bientôt disponible</h3>
                <p className="mt-5 text-sm leading-relaxed text-stone-600">
                  Le don en ligne sécurisé sera activé ultérieurement sur cette page.
                </p>
                <p className="mt-5 text-xs leading-relaxed text-stone-500">
                  Les informations concernant d'éventuels reçus fiscaux seront communiquées uniquement
                  lorsque les éléments juridiques nécessaires seront confirmés.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
